import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import {
  requestIdleCallback,
  viewTransition,
  yieldToMainThread,
} from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Cached page HTML waiting to be rendered.
   *
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * Active page requests, used to prevent the same page being fetched twice.
   *
   * @type {Map<number, Promise<boolean>>}
   */
  pageFetches = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {MutationObserver | undefined} */
  #filterUpdateObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #filterUpdateTimeout;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /**
   * Changes whenever the component is disconnected or its results are updated.
   * This prevents an old page request from modifying newer filtered results.
   *
   * @type {number}
   */
  #contentVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#contentVersion += 1;
    this.#initialiseAspectRatioHelper();
    this.#observeViewMore();

    document.addEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.addEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );

    /*
     * Preload adjacent pages only when the browser is idle.
     * If the customer scrolls quickly, the requested page is still fetched
     * immediately by the render method.
     */
    this.#schedulePrefetch('next');
    this.#schedulePrefetch('previous');
  }

  disconnectedCallback() {
    this.#contentVersion += 1;

    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;

    this.#cleanupFilterUpdateObserver();

    document.removeEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.removeEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );

    this.pages.clear();
    this.pageFetches.clear();

    this.#isRenderingNextPage = false;
    this.#isRenderingPreviousPage = false;

    super.disconnectedCallback();
  }

  /**
   * Creates or refreshes the helper used when new product cards are inserted.
   */
  #initialiseAspectRatioHelper() {
    const templateCard =
      /** @type {HTMLElement | null} */ (
        this.querySelector('[ref="cardGallery"]')
      );

    if (!templateCard) {
      this.#aspectRatioHelper = undefined;
      return;
    }

    this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
      templateCard,
    });
  }

  /**
   * Observes the previous and next page loading markers.
   */
  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    this.infinityScrollObserver?.disconnect();

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          if (!this.isConnected) return;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const {
              viewMorePrevious: currentPreviousMarker,
              viewMoreNext: currentNextMarker,
            } = this.refs;

            if (entry.target === currentPreviousMarker) {
              await this.#renderPreviousPage();
            } else if (entry.target === currentNextMarker) {
              await this.#renderNextPage();
            }
          }
        },
        {
          rootMargin: '100px 0px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Schedules an adjacent page preload without blocking initial rendering.
   *
   * @param {'previous' | 'next'} type
   */
  #schedulePrefetch(type) {
    const version = this.#contentVersion;

    requestIdleCallback(() => {
      if (!this.isConnected || version !== this.#contentVersion) return;

      void this.#fetchPage(type);
    });
  }

  /**
   * Checks whether a page number is valid for the current result set.
   *
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage);

    if (!Number.isFinite(lastPage) || lastPage < 1) return false;
    if (!Number.isFinite(pageInfo.page)) return false;

    return pageInfo.page >= 1 && pageInfo.page <= lastPage;
  }

  /**
   * Fetches the next or previous adjacent page.
   *
   * @param {'previous' | 'next'} type
   * @returns {Promise<boolean>}
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return false;

    return this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * Fetches and caches a specific page.
   *
   * Multiple requests for the same page share one active promise.
   * Old responses are ignored after filters or collection results change.
   *
   * @param {number} pageNumber
   * @param {URL} [url]
   * @returns {Promise<boolean>}
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = {
      page: pageNumber,
      url,
    };

    if (!pageInfo.url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return false;
    if (this.pages.has(pageNumber)) return true;

    const existingRequest = this.pageFetches.get(pageNumber);

    if (existingRequest) {
      return existingRequest;
    }

    const version = this.#contentVersion;

    /** @type {Promise<boolean>} */
    let fetchPromise;

    fetchPromise = (async () => {
      try {
        const pageContent = await sectionRenderer.getSectionHTML(
          this.sectionId,
          true,
          pageInfo.url
        );

        if (!this.isConnected) return false;
        if (version !== this.#contentVersion) return false;
        if (!this.#shouldUsePage(pageInfo)) return false;

        this.pages.set(pageNumber, pageContent);

        return true;
      } catch (error) {
        console.error(
          `Unable to load paginated results page ${pageNumber}.`,
          error
        );

        return false;
      } finally {
        /*
         * Do not let an older completed request delete a newer request for
         * the same page number.
         */
        if (this.pageFetches.get(pageNumber) === fetchPromise) {
          this.pageFetches.delete(pageNumber);
        }
      }
    })();

    this.pageFetches.set(pageNumber, fetchPromise);

    return fetchPromise;
  }

  /**
   * Renders the next collection page.
   */
  async #renderNextPage() {
    if (this.#isRenderingNextPage) return;

    this.#isRenderingNextPage = true;

    const version = this.#contentVersion;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const pageLoaded = await this.#fetchSpecificPage(
          nextPage.page,
          nextPage.url
        );

        if (!pageLoaded) return;
        if (!this.isConnected) return;
        if (version !== this.#contentVersion) return;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
      }

      if (!nextPageItemElements?.length) return;
      if (!this.isConnected) return;
      if (version !== this.#contentVersion) return;

      /*
       * The parsed page is no longer needed after its product cards have
       * been transferred into the live grid.
       */
      this.pages.delete(nextPage.page);

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();

      if (!this.isConnected) return;
      if (version !== this.#contentVersion) return;

      history.pushState('', '', nextPage.url.toString());

      this.#schedulePrefetch('next');
    } catch (error) {
      console.error('Unable to render the next results page.', error);
    } finally {
      this.#isRenderingNextPage = false;
    }
  }

  /**
   * Renders the previous collection page while preserving scroll position.
   */
  async #renderPreviousPage() {
    if (this.#isRenderingPreviousPage) return;

    this.#isRenderingPreviousPage = true;

    const version = this.#contentVersion;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');

      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(
        previousPage.page
      );

      if (!previousPageItemElements) {
        const pageLoaded = await this.#fetchSpecificPage(
          previousPage.page,
          previousPage.url
        );

        if (!pageLoaded) return;
        if (!this.isConnected) return;
        if (version !== this.#contentVersion) return;

        previousPageItemElements = this.#getGridForPage(
          previousPage.page
        );
      }

      if (!previousPageItemElements?.length) return;
      if (!this.isConnected) return;
      if (version !== this.#contentVersion) return;

      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;

      const oldHeight = firstElement
        ? firstElement.getBoundingClientRect().top + currentScrollTop
        : 0;

      this.pages.delete(previousPage.page);

      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      if (firstElement) {
        const newHeight =
          firstElement.getBoundingClientRect().top + getScrollTop();

        const heightDifference = newHeight - oldHeight;

        scrollTo({
          top: currentScrollTop + heightDifference,
          behavior: 'instant',
        });
      }

      await yieldToMainThread();

      if (!this.isConnected) return;
      if (version !== this.#contentVersion) return;

      history.pushState('', '', previousPage.url.toString());

      this.#schedulePrefetch('previous');
    } catch (error) {
      console.error('Unable to render the previous results page.', error);
    } finally {
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * Gets information for the adjacent page.
   *
   * @param {'previous' | 'next'} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards) || cards.length === 0) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);

    if (!Number.isFinite(currentCardPage)) return;

    const page = isPrevious
      ? currentCardPage - 1
      : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * Parses cached HTML and extracts the product cards for one page.
   *
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(
      pageHTML,
      'text/html'
    );

    const gridElement = parsedPage.querySelector('[ref="grid"]');

    if (!gridElement) return;

    const cards = Array.from(
      gridElement.querySelectorAll(':scope > [ref="cards[]"]')
    );

    return cards.length ? cards : undefined;
  }

  /**
   * Removes any temporary observer used while filtered results are replaced.
   */
  #cleanupFilterUpdateObserver() {
    this.#filterUpdateObserver?.disconnect();
    this.#filterUpdateObserver = undefined;

    if (this.#filterUpdateTimeout !== undefined) {
      clearTimeout(this.#filterUpdateTimeout);
      this.#filterUpdateTimeout = undefined;
    }
  }

  /**
   * Reconnects infinite scrolling after a filter or collection update.
   */
  #finishFilterUpdate() {
    this.#cleanupFilterUpdateObserver();

    if (!this.isConnected) return;

    this.#initialiseAspectRatioHelper();
    this.#observeViewMore();

    this.#schedulePrefetch('next');
    this.#schedulePrefetch('previous');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error('The section-id attribute is required');
    }

    return id;
  }

  /**
   * Clears stale page data and waits for filtered results to be replaced.
   *
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventTarget =
      event.target instanceof Element ? event.target : null;

    const eventSection = eventTarget?.closest(
      '[id^="shopify-section-"]'
    );

    if (
      eventSection &&
      eventSection.id !== `shopify-section-${this.sectionId}`
    ) {
      return;
    }

    const previousGrid = this.refs.grid;
    const previousLastPage = previousGrid?.dataset.lastPage;

    this.#contentVersion += 1;

    this.pages.clear();
    this.pageFetches.clear();

    this.#cleanupFilterUpdateObserver();

    this.#filterUpdateObserver = new MutationObserver((mutations) => {
      const currentGrid = this.refs.grid;

      if (!currentGrid) return;

      const gridWasReplaced = currentGrid !== previousGrid;

      const lastPageChanged =
        currentGrid.dataset.lastPage !== previousLastPage;

      const gridContentChanged = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;

        if (
          mutation.target === previousGrid ||
          mutation.target === currentGrid
        ) {
          return true;
        }

        if (!(mutation.target instanceof Element)) return false;

        return (
          mutation.target.closest('[ref="grid"]') === currentGrid
        );
      });

      if (
        !gridWasReplaced &&
        !lastPageChanged &&
        !gridContentChanged
      ) {
        return;
      }

      this.#finishFilterUpdate();
    });

    this.#filterUpdateObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-last-page'],
    });

    /*
     * Fallback in case the theme replaces the results without producing a
     * mutation that matches the expected collection grid.
     */
    this.#filterUpdateTimeout = setTimeout(() => {
      this.#finishFilterUpdate();
    }, 3000);
  };
}